import {
  ApiNav,
  AudiencesNav,
  BatchNav,
  ClearbitIdentity,
  CompaniesNav,
  Config,
  DashboardNav,
  FitCompany,
  Global,
  HelpNav,
  PeopleNav,
  RevealBlock,
  SetupNav,
  UsageAltNav,
  Website,
  ZapAlt,
} from '@clearkit/icons';
import {
  CKNavigationBar,
  CKNavigationBarSwitcher,
  CKNavigationItemProps,
} from 'clearkit';
import { FC, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { NavLink, Route, RouteComponentProps } from 'react-router-dom';
import { Subscribe } from 'unstated';

import { useCurrentUser } from '~/components/profile/useCurrentUser';
import UserContainer from '~/containers/User';
import { AccountFlagEnum } from '~/generated/graphql';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import { useHasUsageAccess } from '~/hooks/account/usePlatformAccess';
import paths from '~/lib/paths';

import DeveloperPanel from '../../DeveloperPanel';
import UserMenu from './UserMenu/UserMenu';

type NavRouterItemProps = Omit<CKNavigationItemProps, 'href' | 'as'> & {
  to: string;
  exact?: boolean;
};

const NavRouterItem: FC<NavRouterItemProps> = ({ to, exact, ...rest }) => {
  return (
    <Route exact={exact} path={to}>
      {({ match }) => {
        const isActive = !!(to && match);

        return (
          <CKNavigationBar.Item
            {...rest}
            as={NavLink}
            isActive={isActive}
            to={to}
          />
        );
      }}
    </Route>
  );
};

type UserContainerType = {
  currentAccount: () => any;
  fetchCurrentUser: () => any;
  shouldShowCompanies: () => boolean;
  shouldShowICP: () => boolean;
  shouldShowDashboard: () => boolean;
  shouldShowSettings: () => boolean;
  shouldShowPeople: () => boolean;
  shouldShowForms: () => boolean;
  hasNoAccess: () => boolean;
  needsToOnboard: () => boolean;
};

type NavBarProps = RouteComponentProps & {
  beta?: boolean;
  view?: string;
  userContainer: UserContainerType | any;
};

type NavBarWrapperProps = Omit<NavBarProps, 'userContainer'>;

const LOCAL_STORAGE_KEY = 'clearbit-navIsExpanded';

const NavBarComponent: FC<NavBarProps> = ({
  userContainer,
  history: {
    location: { pathname, search },
  },
}) => {
  const currentUser = useCurrentUser();

  const localStorageIsExpanded =
    localStorage.getItem(LOCAL_STORAGE_KEY) !== 'false';

  const [showDevPanel, setShowDevPanel] = useState(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(
    localStorageIsExpanded || true,
  );
  const hasUsageFlag = useFeatureFlag(AccountFlagEnum.XShowUsageOnPlatform)
    .enabled;
  const usageEnabled = useHasUsageAccess() && hasUsageFlag;
  const allowPlatformAdvertisingMenu = useFeatureFlag(
    AccountFlagEnum.AllowPlatformAdvertisingMenu,
  ).enabled;

  const handleExpandNavBar = () => {
    setIsExpanded(!isExpanded);
    localStorage.setItem(LOCAL_STORAGE_KEY, String(!isExpanded));
  };

  useEffect(() => {
    setIsExpanded(localStorageIsExpanded);
  }, []);

  const Home = () => (
    <CKNavigationBar.Logo as={NavLink} label="Home" to="/">
      <ClearbitIdentity />
    </CKNavigationBar.Logo>
  );
  Home.displayName = 'CKNavigationBar.Logo';

  const BottomMenu = () => (
    <CKNavigationBar.BottomMenu>
      {process.env.NODE_ENV === 'development' ? (
        <>
          <CKNavigationBar.Item
            label="Dev Tools"
            onClick={() => {
              setShowDevPanel(true);
            }}
          >
            <Config />
          </CKNavigationBar.Item>

          <DeveloperPanel
            onClose={() => {
              setShowDevPanel(false);
            }}
            open={showDevPanel}
          />
        </>
      ) : null}

      <CKNavigationBarSwitcher
        accountName={currentUser?.account?.name ?? ''}
        domain={currentUser?.account?.domain ?? ''}
        name={currentUser?.user?.name ?? ''}
      >
        <CKNavigationBarSwitcher.Menu>
          <UserMenu />
        </CKNavigationBarSwitcher.Menu>
      </CKNavigationBarSwitcher>
    </CKNavigationBar.BottomMenu>
  );
  BottomMenu.displayName = 'CKNavigationBar.BottomMenu';

  if (
    pathname.startsWith('/onboarding') ||
    pathname.startsWith('/get-started') ||
    (pathname.startsWith('/settings/configuration') &&
      userContainer.hasNoAccess()) ||
    // The new free trial will grant platform access, but we still don't want to show the navigation bar sometimes.
    // For example, when updating their CRM settings in grow.clerabit.com or during onboarding.
    // To make it flexible, pass ?navigation=hide in the URL.
    search.includes('navigation=hide')
  ) {
    return null;
  }

  if (userContainer.hasNoAccess()) {
    return (
      <CKNavigationBar isExpanded={isExpanded} onToggle={handleExpandNavBar}>
        <Home />
        <BottomMenu />
      </CKNavigationBar>
    );
  }

  if (pathname === '/welcome' || userContainer.needsToOnboard()) {
    return (
      <CKNavigationBar isExpanded={isExpanded} onToggle={handleExpandNavBar}>
        <Home />
        <CKNavigationBar.Section>
          <NavRouterItem label="Welcome" to="/welcome">
            <SetupNav />
          </NavRouterItem>
        </CKNavigationBar.Section>
        <BottomMenu />
      </CKNavigationBar>
    );
  }

  return (
    <CKNavigationBar isExpanded={isExpanded} onToggle={handleExpandNavBar}>
      <Home />

      <CKNavigationBar.Section>
        <NavRouterItem exact label="Home" to="/">
          <DashboardNav />
        </NavRouterItem>
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        <NavRouterItem label="Target Market" to="/tam-calculator">
          <FitCompany />
        </NavRouterItem>

        <NavRouterItem label="Visitors" to="/visitor-report">
          <Website />
        </NavRouterItem>

        {userContainer.shouldShowPeople() && (
          <NavRouterItem label="People" to="/people">
            <PeopleNav />
          </NavRouterItem>
        )}

        {userContainer.shouldShowCompanies() && (
          <NavRouterItem label="Companies" to="/companies">
            <CompaniesNav />
          </NavRouterItem>
        )}
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        {userContainer.shouldShowSettings() ? (
          <NavRouterItem label="Integrations" to="/integrations">
            <ZapAlt />
          </NavRouterItem>
        ) : null}

        <NavRouterItem label="Batch" to="/batches">
          <BatchNav />
        </NavRouterItem>

        <NavRouterItem exact={false} label="Lookup" to="/lookup">
          <RevealBlock />
        </NavRouterItem>

        <NavRouterItem label="APIs" to="/api">
          <ApiNav />
        </NavRouterItem>
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        {allowPlatformAdvertisingMenu ? (
          <NavRouterItem label="Advertising" to={paths.audiences()}>
            <AudiencesNav />
          </NavRouterItem>
        ) : null}
        <CKNavigationBar.Item
          href="https://chrome.google.com/webstore/detail/clearbit-connect-free-ver/pmnhcgfcafcnkbengdcanjablaabjplo"
          label="Connect"
          target="_blank"
        >
          <Global />
        </CKNavigationBar.Item>
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        {usageEnabled && (
          <NavRouterItem label="Usage" to="/settings/usage">
            <UsageAltNav />
          </NavRouterItem>
        )}

        <CKNavigationBar.Item
          as="a"
          href="https://help.clearbit.com/hc/en-us/"
          label="Help"
          rel="noopener noreferrer"
          target="_blank"
        >
          <HelpNav />
        </CKNavigationBar.Item>
      </CKNavigationBar.Section>

      <BottomMenu />
    </CKNavigationBar>
  );
};

const NavBar: FC<NavBarWrapperProps> = (props) => (
  <Subscribe to={[UserContainer]}>
    {(userContainer) => (
      <NavBarComponent {...props} userContainer={userContainer} />
    )}
  </Subscribe>
);

export default withRouter(NavBar);
